import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Void-a-won-deal approval queue.
 *
 * When a won deal needs to be reopened for an itinerary amendment, an agent
 * raises a PENDING void request routed through the SAME reviewer hierarchy as
 * reprice approvals (requester -> reporting SALES_LEAD -> admin pool; ADMIN
 * auto-approves their own). On APPROVE the service voids the contract (CRM +
 * Documenso), un-wins the lead to its pre-won status, reverts the itinerary to
 * SENT_TO_CUSTOMER, cancels the payment plan/link (preserving any PAID
 * instalment for manual carry-forward), and emails the customer.
 */

const UserRefSchema = z.object({ id: UuidSchema, fullName: z.string() });

// Reuses the reprice status set verbatim (same DB enum).
export const VoidApprovalStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED', 'SUPERSEDED']);
export type VoidApprovalStatus = z.infer<typeof VoidApprovalStatusSchema>;

export const VoidApprovalListItemSchema = z.object({
  id: UuidSchema,
  itineraryId: UuidSchema,
  leadId: UuidSchema.nullable(),
  leadReference: z.string().nullable(),
  customerName: z.string().nullable(),
  itineraryTitle: z.string().nullable(),
  contractId: UuidSchema.nullable(),
  contractTitle: z.string().nullable(),
  status: VoidApprovalStatusSchema,
  requestReason: z.string(),
  /// Paid amount already received (paise, decimal string) at request time -
  /// shown to the reviewer and carried into the customer email as "retained".
  paidAmountPaise: z.string().nullable(),
  reviewRemark: z.string().nullable(),
  requestedBy: UserRefSchema,
  /// When set, this SALES_LEAD reviews the request. Null = admin pool.
  assignedReviewer: UserRefSchema.nullable(),
  reviewedBy: UserRefSchema.nullable(),
  reviewedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type VoidApprovalListItem = z.infer<typeof VoidApprovalListItemSchema>;

export const VoidApprovalDetailSchema = VoidApprovalListItemSchema;
export type VoidApprovalDetail = z.infer<typeof VoidApprovalDetailSchema>;

export const VoidApprovalListQuerySchema = PaginationQuerySchema.extend({
  status: VoidApprovalStatusSchema.optional(),
  requestedById: UuidSchema.optional(),
});
export type VoidApprovalListQuery = z.infer<typeof VoidApprovalListQuerySchema>;

export const VoidApprovalListResponseSchema = paginatedResponseSchema(VoidApprovalListItemSchema);
export type VoidApprovalListResponse = z.infer<typeof VoidApprovalListResponseSchema>;

export const VoidApprovalPendingCountSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type VoidApprovalPendingCount = z.infer<typeof VoidApprovalPendingCountSchema>;

/** Body for `POST /itinerary-void-approvals` (raise a void request). */
export const VoidApprovalCreateRequestSchema = z.object({
  itineraryId: UuidSchema,
  reason: z.string().trim().min(1, 'A reason is required to void a won deal').max(2000),
});
export type VoidApprovalCreateRequest = z.infer<typeof VoidApprovalCreateRequestSchema>;

/** Body for `POST /itinerary-void-approvals/:id/approve`. Remark optional. */
export const VoidApprovalApproveRequestSchema = z.object({
  remark: z.string().trim().max(2000).optional(),
});
export type VoidApprovalApproveRequest = z.infer<typeof VoidApprovalApproveRequestSchema>;

/** Body for `POST /itinerary-void-approvals/:id/reject`. Remark REQUIRED. */
export const VoidApprovalRejectRequestSchema = z.object({
  remark: z.string().trim().min(1, 'A remark is required when rejecting a void request').max(2000),
});
export type VoidApprovalRejectRequest = z.infer<typeof VoidApprovalRejectRequestSchema>;
